/**
 * SingleChoice drill strategy (Phase 3, design §1) — pure ports of today's
 * SingleChoice branches in useStep / StepView / the SingleChoice cards.
 */
import { orderArray } from 'shared/utilities/array';
import { escapeText } from './escapeText';
import type { DrillItemMap, DrillResultRowMap, DrillStrategy } from './types';

type SingleChoiceRow = DrillResultRowMap['SingleChoice'];

/** The fields the solve card's choice builder reads. */
export interface SingleChoiceChoiceSource {
  correct_choice: string;
  wrong_choices?: Array<{ wrong_choice: string }> | null;
}

/**
 * Port of SingleChoice StepSolveCard's `choices` computed, both (identical)
 * branches kept: same `orderArray(…, 'random')` calls in the same order, so a
 * seeded Math.random yields the same choices.
 */
export function singleChoiceChoices(
  item: SingleChoiceChoiceSource | undefined,
  displayChoices = 1,
): string[] {
  if (!item) return [];

  if (!displayChoices) {
    return orderArray(
      [
        ...orderArray(
          item?.wrong_choices?.map((c) => c.wrong_choice) ?? [],
          'random',
        ),
        item.correct_choice,
      ],
      'random',
    );
  }

  return orderArray(
    [
      ...orderArray(
        item.wrong_choices?.map((c) => c.wrong_choice) ?? [],
        'random',
      ),
      item.correct_choice,
    ],
    'random',
  );
}

function correctAnswer(item: DrillItemMap['SingleChoice']): string | null {
  return item && 'correct_choice' in item
    ? (item.correct_choice as string)
    : null;
}

const errorRow: SingleChoiceRow = {
  id: -1,
  source: undefined,
  correct: undefined,
  tries: [],
};

export const singleChoiceStrategy: DrillStrategy<'SingleChoice'> = {
  kind: 'SingleChoice',
  speaksQuestion: true,
  speaksAnswer: true,
  errorRow,

  correctAnswer,

  isCorrect(answer, item) {
    if (answer === undefined || answer === null) return false;
    return answer === correctAnswer(item);
  },

  // HTML members are rendered via v-html: text is escaped (BUG-21).
  feedbackAnswerHtml(answer) {
    return escapeText(answer);
  },

  feedbackCorrectAnswer(item) {
    return escapeText(correctAnswer(item));
  },

  feedbackSubtitle(questionText) {
    return escapeText(questionText);
  },

  helpAnswer(item) {
    return escapeText(correctAnswer(item));
  },

  spokenAnswer(answer) {
    return answer;
  },

  questionText(item) {
    return (item?.source as string | undefined) || '';
  },

  resultRow(item, id, tries) {
    return {
      id,
      source: item?.source as string | undefined,
      correct: item?.correct_choice as string | undefined,
      tries,
    };
  },
};
